import {STATUS, TABS} from '../data/reference'
import {nowMs} from '../data/clock'
import {deadline} from '../format'
import {splitAwbs} from '../data/listBody'

const RANGE_DAYS = {'7d': 7, '30d': 30, '90d': 90}

// Rows within this many kg of each other read as "both govern" — the same
// call the generator itself made pre-reconciliation (excessKg >= 0.5kg =>
// HIGHER_BOTH), just applied to the actual dead/vol figures instead of a
// proxy for them.
const TYPE_TOLERANCE_KG = 0.01

/**
 * The row's dispute type, derived from its OWN deadKg/volKg — never the
 * stored `type` field. Phase A back-solved deadKg/volKg so
 * max(deadKg, volKg) === appliedWeight, but never reconciled the
 * generator's pre-existing `type` (drawn from excessKg plus a coin flip,
 * see scripts/gen-nobero-disputes.js) against that split, so a row can carry
 * type: 'HIGHER_VOL' while deadKg > volKg. Recomputing here — rather than
 * trusting the stored field — is what lets DisputeGrid's display,
 * RecordSections', SkuIntelligence's and state/triage.js's grouping agree
 * by construction instead of by convention: they all call this one
 * function, so they can never drift apart the way the stored field already
 * has.
 */
export function governingType(row) {
  const dead = Number(row && row.deadKg) || 0
  const vol = Number(row && row.volKg) || 0
  if (Math.abs(dead - vol) <= TYPE_TOLERANCE_KG) return 'HIGHER_BOTH'
  return dead > vol ? 'HIGHER_DEAD' : 'HIGHER_VOL'
}

/**
 * Backward-compatible on purpose: `range` is either one of the legacy
 * preset strings ('7d' | '30d' | '90d' | 'all', still exercised by this
 * file's own tests) OR an explicit `{startDate, endDate}` object — what
 * FilterBar's date-range popover now produces via the shared
 * DateRangePicker. A missing/falsy range, 'all', or an object with both
 * bounds null/undefined all mean "no filter". Either bound of an explicit
 * range may be null/undefined for an open-ended side (e.g. "from X onward"
 * or "up to Y"), and an inverted range (endDate before startDate) simply
 * matches nothing — every row fails one of the two independent checks
 * below, so this never throws and never needs a special case for it.
 */
export function inRange(row, range) {
  if (!range || range === 'all') return true

  if (typeof range === 'string') {
    const days = RANGE_DAYS[range]
    if (!days) return true
    return new Date(row.createdAt).getTime() >= nowMs() - days * 86400000
  }

  const created = new Date(row.createdAt).getTime()

  if (range.startDate) {
    if (created < new Date(range.startDate).getTime()) return false
  }

  if (range.endDate) {
    // The end date is inclusive for the whole day it names — a merchant
    // picking "up to 7 Aug" expects rows created any time on 7 Aug to match,
    // not just up to midnight at its start.
    const end = new Date(range.endDate)
    end.setHours(23, 59, 59, 999)
    if (created > end.getTime()) return false
  }

  return true
}

export function queryRows(rows, {tab, filters, range, sort}) {
  const f = filters || {}
  const tabDef = TABS.find((t) => t.id === tab)
  const allowed = tabDef && tabDef.statuses ? new Set(tabDef.statuses) : null
  const q = (f.q || '').trim().toLowerCase()
  const awbs = new Set(splitAwbs(f.awbs).map((s) => s.toLowerCase()))
  const sku = (f.sku || '').trim().toLowerCase()
  const minKg = Number(f.minExcessKg) || 0
  const minAmount = Number(f.minExcessAmount) || 0

  let out = (rows || []).filter((r) => {
    if (allowed && !allowed.has(r.status)) return false
    if (f.status && (!allowed || allowed.has(f.status)) && r.status !== f.status) return false
    // Every filter applies together, the date range included — the same as the server, which only drops
    // its own default window (never a range the screen shows) for an AWB list.
    if (!inRange(r, range)) return false
    if (awbs.size && !awbs.has(String(r.awb || '').toLowerCase())) return false
    if (sku && String(r.sku || '').toLowerCase() !== sku) return false
    if (minKg && !((r.excessKg || 0) >= minKg)) return false
    if (minAmount && !((r.excessAmount || 0) >= minAmount)) return false
    if (f.courier && r.courier !== f.courier) return false
    if (f.urgency) {
      const d = deadline(r.deadlineAt)
      // 'expiring48' has no matching `level` bucket — crit/warn/ok top out at
      // 24h/72h/∞ — so it is checked against `hours` directly instead.
      if (f.urgency === 'expiring48') {
        if (!(d.hours > 0 && d.hours <= 48)) return false
      } else if (d.level !== f.urgency) return false
    }
    if (q && !(r.awb.toLowerCase().includes(q) || r.sku.toLowerCase().includes(q) || r.product.toLowerCase().includes(q) || r.orderId.toLowerCase().includes(q))) return false
    return true
  })

  if (sort === 'excess') out = out.slice().sort((a, b) => b.excessAmount - a.excessAmount)
  else if (sort === 'newest') out = out.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
  else out = out.slice().sort((a, b) => deadline(a.deadlineAt).hours - deadline(b.deadlineAt).hours)

  return out
}

export function paginate(rows, page, perPage) {
  const total = (rows || []).length
  const pages = Math.max(1, Math.ceil(total / perPage))
  const p = Math.min(Math.max(1, page), pages)
  return {items: (rows || []).slice((p - 1) * perPage, p * perPage), total, pages}
}

export function queryTotals(rows) {
  const list = rows || []
  if (!list.length) {
    return {atRisk: 0, expiring48h: 0, recovered: 0, winRate: 0, avgResolutionDays: 0, atRiskCount: 0, recoveredCount: 0, disputedCount: 0, resolvedCount: 0}
  }

  const atRisk = list.reduce((a, r) => a + r.excessAmount, 0)
  const expiring48h = list.filter((r) => {
    const d = deadline(r.deadlineAt)
    return STATUS[r.status].group === 'action' && d.hours > 0 && d.hours <= 48
  }).length

  const won = list.filter((r) => r.status === 'ACCEPTED_BY_COURIER' || r.status === 'ACCEPTED_PROSHIP')
  const decided = list.filter((r) => STATUS[r.status].group === 'resolved')
  const recovered = won.reduce((a, r) => a + r.excessAmount, 0)
  const winRate = decided.length ? Math.round((won.length / decided.length) * 100) : 0

  // A legacy row can carry an updatedAt BEFORE its createdAt (migrated data); that is not a
  // resolution time, and one such row drags the mean below zero (seen live: -3624 days).
  const spans = decided
    .map((r) => (new Date(r.updatedAt) - new Date(r.createdAt)) / 86400000)
    .filter((d) => Number.isFinite(d) && d >= 0)
  const avgResolutionDays = spans.length ? +(spans.reduce((a, b) => a + b, 0) / spans.length).toFixed(1) : 0

  // Shipment counts, one row = one AWB.
  //
  // atRiskCount counts only rows still AWAITING THE MERCHANT — status group
  // 'action'. It used to be list.length, i.e. every row in range, which meant
  // the card read 120 on a dataset where 43 were already resolved and 31
  // already disputed. Nothing is at risk once it has been settled; the number
  // it reported was just "rows in range", under a label that promised urgency.
  // Scoped this way it also equals the Action Required tab's own count, so the
  // card and the tab it opens can never disagree.
  const atRiskCount = list.filter((r) => (STATUS[r.status] || {}).group === 'action').length

  // recoveredCount stays the `won` filter — the ₹ recovered figure, winRate and
  // analytics' recoveredCases all reconcile against it (see analytics.test.js).
  const recoveredCount = won.length

  // resolvedCount is every row the RESOLVED TAB shows — read off that tab's own
  // status list, not STATUS[...].group. The two are not the same set: the group
  // includes ACCEPTED_PROSHIP, which the tab deliberately excludes, so counting
  // the group gave 55 beside a tab reading 43. Deriving it from TABS means the
  // card and the tab it opens cannot drift, including if that list changes.
  //
  // Distinct from recoveredCount on purpose: a card that says "resolved" counts
  // resolutions, not victories — recoveredCount is the won subset.
  const resolvedTab = TABS.find((t) => t.id === 'resolved')
  const resolvedStatuses = new Set((resolvedTab && resolvedTab.statuses) || [])
  const resolvedCount = list.filter((r) => resolvedStatuses.has(r.status)).length

  // Shipments currently in dispute — the same status group the "In Dispute"
  // tab shows (DISPUTED_BY_MERCHANT + DISPUTED_BY_COURIER), read off
  // STATUS[...].group rather than listing the two codes, so adding a third
  // disputed status to data/reference.js would be picked up here for free.
  const disputedCount = list.filter((r) => (STATUS[r.status] || {}).group === 'dispute').length

  // NOTE: `winRate` no longer has a UI consumer — the KPI card that showed it
  // was replaced by the disputed-shipment count. Kept because it costs nothing
  // (`won`/`decided` are already computed for recoveredCount and
  // avgResolutionDays) and it is a metric worth having available.
  return {atRisk, expiring48h, recovered, winRate, avgResolutionDays, atRiskCount, recoveredCount, disputedCount, resolvedCount}
}
